import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    ArrowLeft, User, MapPin, Calendar, Mail, Phone,
    AlertTriangle, Shield, Check, Edit2, X,
} from 'lucide-react';

/* ─── Completion ring SVG ─── */
function CompletionRing({ pct }) {
    const r = 22;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    return (
        <svg width="56" height="56" viewBox="0 0 56 56">
            <circle cx="28" cy="28" r={r} stroke="currentColor" className="pi-ring-track"
                strokeWidth="4" fill="none" />
            <circle cx="28" cy="28" r={r} stroke="currentColor" className="pi-ring-fill"
                strokeWidth="4" fill="none" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={offset}
                transform="rotate(-90 28 28)" />
        </svg>
    );
}

/* ─── Verified pill ─── */
function VerifiedPill({ ok }) {
    if (ok) return <span className="pi-pill pi-pill-ok"><Check size={11} /> Verified</span>;
    return <span className="pi-pill pi-pill-warn"><AlertTriangle size={11} /> Unverified</span>;
}

/* ─── Single editable field row ─── */
function FieldRow({ icon: Icon, label, value, type = 'text', placeholder, suffix, onSave, multiline }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing) {
            setDraft(value ?? '');
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [editing]);

    // Sync when parent value changes (e.g. after reload)
    useEffect(() => {
        if (!editing) setDraft(value ?? '');
    }, [value]);

    const save = () => { onSave?.(draft); setEditing(false); };
    const cancel = () => { setDraft(value ?? ''); setEditing(false); };
    const isEmpty = !value;

    return (
        <div className={`pi-row${editing ? ' is-editing' : ''}`}>
            <div className="pi-row-label">
                {Icon && <Icon size={14} />}
                <span>{label}</span>
            </div>

            {editing ? (
                <div className="pi-row-edit-wrap">
                    {multiline ? (
                        <textarea
                            ref={inputRef}
                            className="pi-input"
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            placeholder={placeholder}
                            rows={3}
                        />
                    ) : (
                        <input
                            ref={inputRef}
                            type={type}
                            className="pi-input"
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            placeholder={placeholder}
                            onKeyDown={e => {
                                if (e.key === 'Enter') save();
                                if (e.key === 'Escape') cancel();
                            }}
                        />
                    )}
                    <div className="pi-row-actions">
                        <button className="ix-btn ix-btn-ghost ix-btn-sm" onClick={cancel}>Cancel</button>
                        <button className="ix-btn ix-btn-primary ix-btn-sm" onClick={save}>Save</button>
                    </div>
                </div>
            ) : (
                <button className="pi-row-display" onClick={() => setEditing(true)}>
                    <span className={`pi-row-value${isEmpty ? ' is-empty' : ''}`}>
                        {value || placeholder || 'Not set'}
                    </span>
                    <span className="pi-row-suffix">
                        {suffix}
                        <span className="pi-edit-hint"><Edit2 size={12} /></span>
                    </span>
                </button>
            )}
        </div>
    );
}

/* ─── Select field row (for dropdowns) ─── */
function SelectFieldRow({ icon: Icon, label, value, options, placeholder, onSave, disabled, autoSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');

    useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);

    const save = () => { onSave?.(draft); setEditing(false); };
    const cancel = () => { setDraft(value ?? ''); setEditing(false); };

    const handleChange = e => {
        const v = e.target.value;
        setDraft(v);
        if (autoSave && v) { onSave?.(v); setEditing(false); }
    };

    return (
        <div className={`pi-row${editing ? ' is-editing' : ''}`}>
            <div className="pi-row-label">
                {Icon && <Icon size={14} />}
                <span>{label}</span>
            </div>
            {editing ? (
                <div className="pi-row-edit-wrap">
                    <select
                        className="pi-input"
                        value={draft}
                        onChange={handleChange}
                        disabled={disabled}
                        autoFocus
                    >
                        <option value="">{placeholder || 'Select…'}</option>
                        {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {!autoSave && (
                        <div className="pi-row-actions">
                            <button className="ix-btn ix-btn-ghost ix-btn-sm" onClick={cancel}>Cancel</button>
                            <button className="ix-btn ix-btn-primary ix-btn-sm" onClick={save} disabled={!draft}>Save</button>
                        </div>
                    )}
                </div>
            ) : (
                <button className="pi-row-display" onClick={() => !disabled && setEditing(true)}>
                    <span className={`pi-row-value${!value ? ' is-empty' : ''}`}>
                        {value || placeholder || 'Not set'}
                    </span>
                    <span className="pi-row-suffix">
                        <span className="pi-edit-hint"><Edit2 size={12} /></span>
                    </span>
                </button>
            )}
        </div>
    );
}

/* ─── Country + City location rows ─── */
function LocationRows({ country, city, onSaveCountry, onSaveCity }) {
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);

    useEffect(() => {
        axios.get('https://countriesnow.space/api/v0.1/countries/iso')
            .then(r => setCountries((r.data?.data ?? []).map(c => c.name)))
            .catch(() => {});
    }, []);

    const fetchCities = useCallback((c) => {
        if (!c) { setCities([]); return; }
        setCitiesLoading(true);
        axios.post('https://countriesnow.space/api/v0.1/countries/cities', { country: c })
            .then(r => setCities(r.data?.data ?? []))
            .catch(() => setCities([]))
            .finally(() => setCitiesLoading(false));
    }, []);

    useEffect(() => { fetchCities(country); }, [country, fetchCities]);

    return (
        <>
            <SelectFieldRow
                icon={MapPin}
                label="Country"
                value={country}
                options={countries}
                placeholder="Select country…"
                autoSave
                onSave={v => { onSaveCountry(v); fetchCities(v); }}
            />
            <SelectFieldRow
                label="City"
                value={city}
                options={cities}
                placeholder={citiesLoading ? 'Loading cities…' : country ? 'Select city…' : 'Select country first'}
                disabled={!country || citiesLoading}
                autoSave
                onSave={onSaveCity}
            />
        </>
    );
}

/* ─── Section wrapper ─── */
function PISection({ title, sub, children }) {
    return (
        <section className="pi-section">
            <header className="pi-section-head">
                <h2 className="pi-section-title">{title}</h2>
                {sub && <p className="pi-section-sub">{sub}</p>}
            </header>
            <div className="pi-rows">{children}</div>
        </section>
    );
}

/* ─── Change password modal ─── */
function ChangePasswordModal({ userId, token, onClose, onSuccess }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (next.length < 6) { setError('New password must be at least 6 characters.'); return; }
        if (next !== confirm) { setError('Passwords do not match.'); return; }
        setSaving(true);
        setError('');
        try {
            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/${userId}/change-password`,
                { currentPassword: current, newPassword: next },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            onSuccess();
            onClose();
        } catch (e) {
            const msg = e.response?.data;
            setError(typeof msg === 'string' ? msg : 'Failed to change password.');
        }
        setSaving(false);
    };

    return (
        <div className="pi-pw-modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="pi-pw-modal">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 className="pi-pw-modal-title">Change password</h3>
                    <button className="ix-btn ix-btn-ghost" style={{ padding: '4px' }} onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="pi-pw-field">
                    <label>Current password</label>
                    <input type="password" className="pi-input" value={current}
                        onChange={e => setCurrent(e.target.value)} placeholder="Your current password" />
                </div>
                <div className="pi-pw-field">
                    <label>New password</label>
                    <input type="password" className="pi-input" value={next}
                        onChange={e => setNext(e.target.value)} placeholder="At least 6 characters" />
                </div>
                <div className="pi-pw-field">
                    <label>Confirm new password</label>
                    <input type="password" className="pi-input" value={confirm}
                        onChange={e => setConfirm(e.target.value)} placeholder="Repeat new password"
                        onKeyDown={e => e.key === 'Enter' && submit()} />
                </div>

                {error && <p className="pi-pw-error">{error}</p>}

                <div className="pi-pw-actions">
                    <button className="ix-btn ix-btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="ix-btn ix-btn-primary" onClick={submit} disabled={saving}>
                        {saving ? 'Saving…' : 'Update password'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Completion calculator ─── */
function computeCompletion(u) {
    const checks = [
        { key: 'firstname',   label: 'Add first name' },
        { key: 'lastname',    label: 'Add last name' },
        { key: 'dateOfBirth', label: 'Add date of birth' },
        { key: 'email',       label: 'Add email' },
        { key: 'phoneNumber', label: 'Add phone number' },
        { key: 'addressLine1', label: 'Add address' },
        { key: 'city',        label: 'Add city' },
        { key: 'postcode',    label: 'Add postcode' },
        { key: 'emergencyContactName',  label: 'Add emergency contact name' },
        { key: 'emergencyContactPhone', label: 'Add emergency phone' },
    ];
    const filled = checks.filter(c => !!u[c.key]).length;
    const pct = Math.round((filled / checks.length) * 100);
    const missing = checks.find(c => !u[c.key]);
    return { pct, next: missing?.label };
}

/* ─── Main component ─── */
const PersonalInfo = ({ user }) => {
    const [profileUser, setProfileUser] = useState(null);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [toast, setToast]             = useState(false);
    const [showPwModal, setShowPwModal] = useState(false);
    const navigate = useNavigate();

    const token   = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const authHdr = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        if (!user?.id) return;
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/id/${user.id}`, { headers: authHdr })
            .then(r => setProfileUser(r.data))
            .catch(() => setError('Unable to load your details.'))
            .finally(() => setLoading(false));
    }, [user]);

    const showToast = () => {
        setToast(true);
        setTimeout(() => setToast(false), 2000);
    };

    /* Save a single field */
    const save = async (fieldKey, value) => {
        try {
            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/update/${user.id}`,
                { [fieldKey]: value },
                { headers: { ...authHdr, 'Content-Type': 'application/json' } }
            );
            setProfileUser(u => ({ ...u, [fieldKey]: value }));
            showToast();
        } catch {
            alert('Failed to save. Please try again.');
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px', color: 'var(--ink-500)' }}>
            Loading…
        </div>
    );
    if (error || !profileUser) return (
        <div style={{ textAlign: 'center', padding: '80px', color: '#b91c1c' }}>
            {error || 'User not found.'}
        </div>
    );

    const completion = computeCompletion(profileUser);
    const emailVerified = !!profileUser.email;
    const phoneVerified = profileUser.phoneNumber && profileUser.phoneNumber !== '+44';

    return (
        <div className="pi-page">
            <main className="pi-main">
                <div className="ix-container" style={{ maxWidth: '820px' }}>

                    {/* Back */}
                    <Link to={`/profile/${user.id}`} className="pi-back">
                        <ArrowLeft size={14} /> Back to Profile
                    </Link>

                    {/* Page header */}
                    <header className="pi-pagehead">
                        <div>
                            <div className="pi-eyebrow">Account · Personal information</div>
                            <h1 className="pi-title">Your details</h1>
                            <p className="pi-lede">
                                Only you and Interim see this page. Posters only see your public profile.
                            </p>
                        </div>

                        {/* Completion ring */}
                        <div className="pi-completion">
                            <CompletionRing pct={completion.pct} />
                            <div>
                                <div className="pi-completion-label">Profile completion</div>
                                <div className="pi-completion-pct">{completion.pct}%</div>
                                {completion.next && (
                                    <div className="pi-completion-next">{completion.next}</div>
                                )}
                            </div>
                        </div>
                    </header>

                    <div className="pi-grid">

                        {/* ── Identity ── */}
                        <PISection
                            title="Identity"
                            sub="Use your legal name as it appears on ID — used for contracts and payouts."
                        >
                            <FieldRow icon={User} label="First name"
                                value={profileUser.firstname} placeholder="e.g. Askar"
                                onSave={v => save('firstname', v)} />
                            <FieldRow icon={User} label="Last name"
                                value={profileUser.lastname} placeholder="e.g. Kazhybay"
                                onSave={v => save('lastname', v)} />
                            <FieldRow icon={Calendar} label="Date of birth"
                                value={profileUser.dateOfBirth} placeholder="YYYY-MM-DD" type="date"
                                onSave={v => save('dateOfBirth', v)} />
                        </PISection>

                        {/* ── Contact ── */}
                        <PISection
                            title="Contact"
                            sub="How posters and Interim get in touch. Verified channels show a green tick on your applications."
                        >
                            <FieldRow label="Email"
                                value={profileUser.email} placeholder="you@example.com" type="email"
                                suffix={<VerifiedPill ok={emailVerified} />}
                                onSave={v => save('email', v)} />
                            <FieldRow icon={Phone} label="Phone"
                                value={profileUser.phoneNumber} placeholder="+353 87..." type="tel"
                                suffix={<VerifiedPill ok={phoneVerified} />}
                                onSave={v => save('phoneNumber', v)} />
                            <FieldRow icon={MapPin} label="Address line 1"
                                value={profileUser.addressLine1} placeholder="123 Main Street"
                                onSave={v => save('addressLine1', v)} />
                            <FieldRow icon={MapPin} label="Address line 2"
                                value={profileUser.addressLine2} placeholder="Flat / floor (optional)"
                                onSave={v => save('addressLine2', v)} />
                            <LocationRows
                                country={profileUser.country}
                                city={profileUser.city}
                                onSaveCountry={v => save('country', v)}
                                onSaveCity={v => save('city', v)}
                            />
                            <FieldRow label="Postcode / Eircode"
                                value={profileUser.postcode} placeholder="D01 AB12"
                                onSave={v => save('postcode', v)} />
                        </PISection>

                        {/* ── Emergency contact ── */}
                        <PISection
                            title="Emergency contact"
                            sub="Only used if something goes wrong on a job. Never shared with posters."
                        >
                            <FieldRow icon={User} label="Name"
                                value={profileUser.emergencyContactName} placeholder="Full name"
                                onSave={v => save('emergencyContactName', v)} />
                            <FieldRow label="Relationship"
                                value={profileUser.emergencyContactRelationship}
                                placeholder="e.g. Partner, parent, friend"
                                onSave={v => save('emergencyContactRelationship', v)} />
                            <FieldRow icon={Phone} label="Phone"
                                value={profileUser.emergencyContactPhone} placeholder="+353 87..." type="tel"
                                onSave={v => save('emergencyContactPhone', v)} />
                        </PISection>

                        {/* ── Security ── */}
                        <PISection title="Security">
                            <div className="pi-row pi-row-action">
                                <div className="pi-row-label">
                                    <Shield size={14} /> <span>Password</span>
                                </div>
                                <div className="pi-row-display">
                                    <span className="pi-row-value mono" style={{ letterSpacing: '0.1em', color: 'var(--ink-300)' }}>
                                        ••••••••••••
                                    </span>
                                    <button
                                        className="ix-btn ix-btn-outline ix-btn-sm"
                                        onClick={() => setShowPwModal(true)}
                                    >
                                        Change password
                                    </button>
                                </div>
                            </div>
                        </PISection>

                    </div>
                </div>
            </main>

            {/* ── Change password modal ── */}
            {showPwModal && (
                <ChangePasswordModal
                    userId={user.id}
                    token={token}
                    onClose={() => setShowPwModal(false)}
                    onSuccess={showToast}
                />
            )}

            {/* ── Saved toast ── */}
            <div className={`pi-toast${toast ? ' is-on' : ''}`}>
                <Check size={14} /> Saved
            </div>
        </div>
    );
};

export default PersonalInfo;
